import React, { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import AdminLayout from "../layouts/AdminLayout";
import "./AdminContactInfoPage.css";

const API_BASE = "https://api.mariamlyluxe.com/public";

export default function AdminContactInfoPage() {
  const [contactInfo, setContactInfo] = useState({
    phone: "",
    email: "",
    location: "",
    instagram: ""
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});

  // Get auth token
  const getAuthToken = () => {
    return localStorage.getItem("access_token") || localStorage.getItem("token");
  };

  // Fetch current contact info
  const fetchContactInfo = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/admin/contact-info`, {
        headers: {
          "Authorization": `Bearer ${getAuthToken()}`,
          "Accept": "application/json"
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success && data.contact) {
        setContactInfo({
          phone: data.contact.phone || "",
          email: data.contact.email || "",
          location: data.contact.location || "",
          instagram: data.contact.instagram || ""
        });
      }
    } catch (error) {
      console.error("Error fetching contact info:", error);
      toast.error("Failed to load contact info");
    } finally {
      setIsLoading(false);
    }
  };

  // Update contact info
  const updateContactInfo = async () => {
    // Validate form
    const newErrors = {};
    
    if (!contactInfo.phone.trim()) {
      newErrors.phone = "Phone number is required";
    }
    
    if (!contactInfo.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactInfo.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    
    if (!contactInfo.location.trim()) {
      newErrors.location = "Location is required";
    }
    
    // Instagram validation (optional field)
    if (contactInfo.instagram.trim() && !contactInfo.instagram.match(/^@?[a-zA-Z0-9._]+$/)) {
      newErrors.instagram = "Please enter a valid Instagram handle (e.g., @username or username)";
    }
    
    setErrors(newErrors);
    
    if (Object.keys(newErrors).length > 0) {
      toast.error("Please fix the errors below");
      return;
    }

    setIsSaving(true);
    try {
      const response = await fetch(`${API_BASE}/api/admin/contact-info`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${getAuthToken()}`,
          "Accept": "application/json"
        },
        body: JSON.stringify(contactInfo)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Request failed: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        toast.success("Contact info updated successfully!");
        // Clear any cache by refetching
        await fetchContactInfo();
      } else {
        throw new Error(data.message || "Failed to update contact info");
      }
    } catch (error) {
      console.error("Error updating contact info:", error);
      toast.error(error.message || "Failed to update contact info");
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setContactInfo(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  useEffect(() => {
    fetchContactInfo();
  }, []);

  return (
    <AdminLayout>
      <div className="admin-contact-info-page">
        <div className="page-header">
          <h1>Contact Information</h1>
          <p>Manage the contact information displayed on the website</p>
        </div>

      <div className="contact-info-card">
        <div className="card-header">
          <h2>Contact Details</h2>
          <button 
            className="refresh-btn"
            onClick={fetchContactInfo}
            disabled={isLoading}
          >
            {isLoading ? "Loading..." : "Refresh"}
          </button>
        </div>

        <div className="form-section">
          <div className={`form-group ${errors.phone ? 'error' : ''}`}>
            <label htmlFor="phone">Phone Number *</label>
            <input
              type="text"
              id="phone"
              name="phone"
              value={contactInfo.phone}
              onChange={handleInputChange}
              placeholder="+961-70 457 945"
              disabled={isLoading}
            />
            {errors.phone && <div className="error-message">{errors.phone}</div>}
          </div>

          <div className={`form-group ${errors.email ? 'error' : ''}`}>
            <label htmlFor="email">Email Address *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={contactInfo.email}
              onChange={handleInputChange}
              placeholder="info@mariamlyluxe.com"
              disabled={isLoading}
            />
            {errors.email && <div className="error-message">{errors.email}</div>}
          </div>

          <div className={`form-group ${errors.location ? 'error' : ''}`}>
            <label htmlFor="location">Location *</label>
            <input
              type="text"
              id="location"
              name="location"
              value={contactInfo.location}
              onChange={handleInputChange}
              placeholder="Tripoli"
              disabled={isLoading}
            />
            {errors.location && <div className="error-message">{errors.location}</div>}
          </div>

          <div className={`form-group ${errors.instagram ? 'error' : ''}`}>
            <label htmlFor="instagram">Instagram Handle</label>
            <input
              type="text"
              id="instagram"
              name="instagram"
              value={contactInfo.instagram}
              onChange={handleInputChange}
              placeholder="@mariamly.luxe or mariamly.luxe"
              disabled={isLoading}
            />
            {errors.instagram && <div className="error-message">{errors.instagram}</div>}
            <small className="field-help">Optional. Enter Instagram handle with or without @ symbol</small>
          </div>

          <div className="form-actions">
            <button 
              className="save-btn"
              onClick={updateContactInfo}
              disabled={isSaving || isLoading}
            >
              {isSaving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>

        <div className="preview-section">
          <h3>Preview</h3>
          <div className="contact-preview">
            <div className="preview-item">
              <span className="preview-icon">📱</span>
              <span>{contactInfo.phone || "Phone number"}</span>
            </div>
            <div className="preview-item">
              <span className="preview-icon">✉️</span>
              <span>{contactInfo.email || "Email address"}</span>
            </div>
            <div className="preview-item">
              <span className="preview-icon">📍</span>
              <span>{contactInfo.location || "Location"}</span>
            </div>
            {contactInfo.instagram && (
              <div className="preview-item">
                <span className="preview-icon">📷</span>
                <span>{contactInfo.instagram}</span>
              </div>
            )}
          </div>
        </div>
      </div>
      </div>
    </AdminLayout>
  );
}
